<script setup lang="ts">
import { ref } from 'vue';
import AppButton from './AppButton.vue';
import { useSessionTimer } from '@/composables/use-session-timer';
import { useAuthStore } from '@/stores/auth.store';

const auth = useAuthStore();
const { countdown, isWarning, canExtend, isExtending, extend } = useSessionTimer();

const hasError = ref(false);

async function staySignedIn() {
  hasError.value = false;
  const renewed = await extend();
  if (!renewed) hasError.value = true;
}
</script>

<template>
  <!-- role="alert" announces the static message once. The ticking numerals sit in an
       aria-hidden element so a screen reader is not read a new time every second. -->
  <div
    v-if="isWarning"
    role="alert"
    class="pointer-events-auto w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-amber-300 bg-white p-4 shadow-lg md:w-96"
  >
    <div class="flex items-start gap-3">
      <svg
        class="mt-0.5 h-5 w-5 shrink-0 text-amber-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="2"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M12 6v6l4 2m5-2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold text-slate-900">
          {{ canExtend ? 'Still there?' : 'Your session is ending' }}
        </p>

        <p
          v-if="canExtend"
          class="mt-1 text-sm text-slate-600"
        >
          You'll be signed out automatically in
          <span
            aria-hidden="true"
            class="font-mono font-semibold tabular-nums text-slate-900"
          >{{ countdown }}</span>.
        </p>
        <p
          v-else
          class="mt-1 text-sm text-slate-600"
        >
          This session has reached its time limit and can't be extended. Save anything you're
          working on now — you'll be signed out in
          <span
            aria-hidden="true"
            class="font-mono font-semibold tabular-nums text-slate-900"
          >{{ countdown }}</span>, then you can sign in again.
        </p>

        <p
          v-if="hasError"
          class="mt-2 text-sm text-red-700"
        >
          We couldn't renew your session. Check your connection and try again.
        </p>

        <div class="mt-3 flex flex-wrap gap-2">
          <AppButton
            v-if="canExtend"
            variant="primary"
            class="min-h-[2.75rem]"
            :loading="isExtending"
            @click="staySignedIn"
          >
            Stay signed in
          </AppButton>
          <AppButton
            variant="secondary"
            class="min-h-[2.75rem]"
            @click="auth.signOut()"
          >
            Sign out now
          </AppButton>
        </div>
      </div>
    </div>
  </div>
</template>
